import { useMemo, useCallback } from 'react';
import { useTranslation } from '@packlink/translation-provider';
import { FastField } from 'formik';
import { Button, ButtonVariant, ButtonColor, ButtonSize, FieldMessageType } from '@shipengine/giger';
import { Currency } from '@packlink/packlink-sdk';
import { useCustomsFormContext } from '../../../CustomsFormData.js';
import { getComposedFieldName } from '@packlink/utils';
import { TariffNumberFields } from './TariffNumberFields.js';
import { FormField, Textarea, Input } from '@shipengine/formik-giger';
import { FormikCountrySelector } from '@packlink/address-selectors';
import { getInventoryItemInlineStyles } from './InventoryItemStyles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { CustomsSection, CustomsSectionVariant } from '../../Partials/CustomsSection.js';

function InventoryItem(_ref) {
  let {
    index,
    currency,
    onRemoveItem,
    ...props
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    fieldNamePrefix,
    locale
  } = useCustomsFormContext();
  const currencySymbol = useMemo(() => Currency.getCurrencySymbol(currency), [currency]);
  const removeItem = useCallback(() => {
    onRemoveItem(index);
  }, [index, onRemoveItem]);
  const getFieldName = field => getComposedFieldName(fieldNamePrefix, `inventoryOfContents[${index}]`, field);
  const canRemoveItem = index > 0;
  return jsxs(CustomsSection, {
    title: `${t('inventory-item.header.title')} ${index + 1}`,
    variant: CustomsSectionVariant.SUBSECTION,
    parent: "article",
    rightContent: canRemoveItem && jsx(Button, {
      onClick: removeItem,
      variant: ButtonVariant.TEXT,
      color: ButtonColor.ERROR,
      size: ButtonSize.SMALL,
      children: t('inventory-item.label.remove')
    }),
    ...props,
    children: [jsx(TariffNumberFields, {
      index: index
    }), jsx(FormField, {
      name: getFieldName('description'),
      message: {
        content: t('inventory-item.placeholder.description'),
        type: FieldMessageType.HINT
      },
      children: jsx(FastField, {
        component: Textarea,
        label: t('inventory-item.label.description'),
        type: "textarea"
      })
    }), jsx(FastField, {
      component: FormikCountrySelector,
      name: getFieldName('countryOfOrigin'),
      label: t('inventory-item.label.country-of-origin'),
      locale: locale
    }), jsxs("div", {
      css: getInventoryItemInlineStyles,
      children: [jsx(FormField, {
        name: getFieldName('numberOfItems'),
        message: {
          content: t('inventory-item.placeholder.number-of-items'),
          type: FieldMessageType.HINT
        },
        children: jsx(FastField, {
          component: Input,
          type: "number",
          label: t('inventory-item.label.number-of-items')
        })
      }), jsx(FormField, {
        name: getFieldName('value'),
        message: {
          content: t('inventory-item.placeholder.value'),
          type: FieldMessageType.HINT
        },
        children: jsx(FastField, {
          component: Input,
          type: "number",
          step: "0.01",
          label: t('inventory-item.label.value'),
          helper: currencySymbol
        })
      }), jsx(FormField, {
        name: getFieldName('weight'),
        message: {
          content: t('inventory-item.placeholder.weight'),
          type: FieldMessageType.HINT
        },
        children: jsx(FastField, {
          component: Input,
          type: "number",
          step: "0.01",
          label: t('inventory-item.label.weight'),
          helper: "kg"
        })
      })]
    })]
  });
}

export { InventoryItem };
//# sourceMappingURL=InventoryItem.js.map
